function GettingStarted() {
  return (
    <section aria-labelledby="before-you-start-heading">
      <h3 id="before-you-start-heading">Before you start</h3>
      <ul>
        <li>
          Select your <strong>Data Source</strong> from the options
          listed. If an appropriate source does not exist, see the{" "}
          <strong>Manage Sources</strong> tab to add one.
        </li>
        <li>
          Prepare your <strong>CSV</strong> using the Contact Import
          Sheet Template provided. You can download this from the Data
          Import Tool program.
        </li>
        <li>
          Ensure each profile has a unique <strong>reference</strong>{" "}
          (see column A of the template). The reference can be any
          alphanumeric value.
        </li>
      </ul>
    </section>
  );
}

function TheProcess() {
  return (
    <>
      <section aria-labelledby="the-process-heading">
        <h3 id="the-process-heading">Steps</h3>
        <ol>
          <li>
            <strong>Select Source:</strong> Choose the source for the
            import.
          </li>
          <li>
            <strong>Drop CSV:</strong> Drag and drop the CSV file into
            the drop zone.
          </li>
          <li>
            <strong>Click Import:</strong> Starts the import process
            into the Data Warehouse.
          </li>
        </ol>
      </section>

      <section aria-labelledby="system-steps-heading">
        <h3 id="system-steps-heading">
          What happens after you click <em>Import</em>
        </h3>

        <ol class="process-steps">
          <li>
            <h4>Parse the CSV</h4>
            <p>
              The CSV is read and validated to identify the{" "}
              <strong>profiles to import</strong>. During this stage
              the system checks:
            </p>
            <ul className="mb-3">
              <li>
                <strong>Presence of profiles</strong> (at least one
                valid row).
              </li>
              <li>
                <strong>Reference field</strong> exists for each
                profile.
              </li>
              <li>
                <strong>Reference uniqueness</strong> (no duplicates
                within the file).
              </li>
            </ul>
          </li>

          <li>
            <h4>Early stop conditions</h4>
            <p>
              If any of the following are detected, the import{" "}
              <strong>stops immediately</strong>:
            </p>
            <ul className="mb-3">
              <li>
                <strong>No profiles found</strong> in the CSV.
              </li>
              <li>
                <strong>Profiles without a reference</strong> value.
              </li>
              <li>
                <strong>Duplicated reference</strong> values detected.
              </li>
            </ul>
          </li>

          <li>
            <h4>Create & Clean the Profiles</h4>
            <p>
              Once the import is validated, profiles are created, then
              cleaned to standardise and normalise the data ready for
              the Data Warehouse. The cleaning process includes:
            </p>
            <ul className="mb-3">
              <li>Name formatting and corrections</li>
              <li>Phone number formatting</li>
              <li>Address formatting and corrections</li>
              <li>Gender cleanup and normalisation</li>
              <li>Birthdate formatting and validation</li>
            </ul>
          </li>

          <li>
            <h4>Add Profiles to the Warehouse</h4>
            <p>
              After cleaning, each profile is processed against the
              warehouse. Based on your club defined{" "}
              <strong>match keys</strong>, one of the following
              outcomes occurs:
            </p>
            <ul className="mb-3">
              <li>New profile created</li>
              <li>Existing profile updated</li>
              <li>
                Existing profile detected but no changes required
              </li>
              <li>
                Profile rejected due to insufficient data (missing
                data based on match keys)
              </li>
            </ul>
            <p>
              When a <strong>new profile</strong> is created, a new{" "}
              <strong>Contact</strong> is also created and associated
              with that profile automatically.
            </p>
          </li>

          <li>
            <h4>Synchronise with Gantry</h4>
            <p>
              Once profiles have been fully processed into the
              warehouse, the records are synchronised into the Gantry
              database. During this stage:
            </p>
            <ul className="mb-3">
              <li>
                Any <strong>newly created</strong> profiles also have
                a corresponding <strong>Gantry profile</strong>{" "}
                created and inserted
              </li>
              <li>All processed profiles are pushed to Gantry</li>
              <li>
                If a contact was previously linked to Gantry but that
                link can no longer be confirmed, the row is flagged
                with an <strong>error</strong> rather than creating a
                duplicate contact in Gantry
              </li>
            </ul>
          </li>

          <li>
            <h4>Create Gantry Static Marketing List</h4>
            <p>
              After Gantry synchronisation completes, a new{" "}
              <strong>Static Marketing List</strong> is automatically
              created in Gantry.
            </p>
            <p>
              This list contains{" "}
              <strong>all processed contacts</strong> from the import
              and can be used immediately for segmentation or
              campaigns.
            </p>
          </li>
        </ol>
      </section>
    </>
  );
}

function FAQ() {
  return (
    <section>
      <ul className="list-unstyled">
        <li>
          <h4>What is defined as a duplicate here?</h4>
          <p>
            Profiles created from the import which match to the same
            existing contact. There could be multiple rows in the
            import which belong to the same contact.
          </p>
        </li>
        <li>
          <h4>
            Why does a row show an error about being unable to match
            to Gantry?
          </h4>
          <p>
            This means the contact was linked to a Gantry contact
            previously, but that link could not be confirmed during
            this import. Rather than risk creating a duplicate
            contact in Gantry, the row is left unchanged and flagged
            as an error for review.
          </p>
        </li>
        <li>
          <h4>
            If my data import file exceeds 15MB then what do I do?
          </h4>
          <p>
            If the file exceeds that then the file would need to be
            split. A file with 5,000 contacts is typically around
            500kb, meaning a limit of around 150,000 contacts for a
            single import.
          </p>
        </li>
      </ul>
    </section>
  );
}

export { FAQ, GettingStarted, TheProcess };
